import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { IxcRegistrationV1Affiliations, IxcRegistrationV1Name } from '@interxfi/types/generated/api';
import { addDefinedValues } from '../../../../../../util/has-value';
import type { NameFormValues } from '../../../../shared-form/name-form';
import {
  AssociatedPersonsForm,
  associatedPersonsFormSchema,
  formValuesToAssociatedPersons,
  getInitialFormValuesForAssociatedPersons,
} from './associated-persons-form';
import {
  CorporateInsiderForm,
  corporateInsiderFormSchema,
  formValuesToCorporateInsider,
  getInitialFormValuesForCorporateInsider,
} from './corporate-insider-form';
import {
  formValuesToPoliticallyExposedPerson,
  getInitialFormValuesForPoliticallyExposedPerson,
  PoliticallyExposedPersonForm,
  politicallyExposedPersonFormSchema,
} from './politically-exposed-person-form';

export const affiliationsFormSchema = z.object({
  politicallyExposed: politicallyExposedPersonFormSchema,
  corporateInsider: corporateInsiderFormSchema,
  associatedPersons: associatedPersonsFormSchema,
});

export type AffiliationsFormValues = z.infer<typeof affiliationsFormSchema>;

export const defaultAffiliationsFormValues: AffiliationsFormValues = {
  politicallyExposed: {
    isProviding: false,
  },
  corporateInsider: {
    isProviding: false,
  },
  associatedPersons: {
    isProviding: false,
    value: [],
  },
};

export function getInitialFormValuesForAffiliations(
  affiliations: IxcRegistrationV1Affiliations | undefined,
  profileName: IxcRegistrationV1Name | undefined,
): AffiliationsFormValues {
  return addDefinedValues<AffiliationsFormValues>(
    {
      ...defaultAffiliationsFormValues,
    },
    {
      politicallyExposed: getInitialFormValuesForPoliticallyExposedPerson(affiliations?.politicallyExposedPerson),
      corporateInsider: getInitialFormValuesForCorporateInsider(affiliations?.corporateInsider),
      associatedPersons: getInitialFormValuesForAssociatedPersons(affiliations?.associatedPersons, profileName),
    },
  );
}

export function formValuesToAffiliations(
  values: AffiliationsFormValues | undefined,
  profileName: NameFormValues | undefined,
): IxcRegistrationV1Affiliations | undefined {
  if (!values) {
    return undefined;
  }

  const affiliations: IxcRegistrationV1Affiliations = {};

  const politicallyExposed = formValuesToPoliticallyExposedPerson(values.politicallyExposed);
  const corporateInsider = formValuesToCorporateInsider(values.corporateInsider);
  const associatedPersons = formValuesToAssociatedPersons(values.associatedPersons, profileName);

  if (politicallyExposed) {
    affiliations.politicallyExposedPerson = politicallyExposed;
  }

  if (corporateInsider) {
    affiliations.corporateInsider = corporateInsider;
  }

  if (associatedPersons) {
    affiliations.associatedPersons = associatedPersons;
  }

  return Object.keys(affiliations).length > 0 ? affiliations : undefined;
}

interface AffiliationsFormProps {
  lens: Lens<AffiliationsFormValues>;
}

export function AffiliationsForm({ lens }: AffiliationsFormProps) {
  return (
    <VerticalStack gap={7}>
      <AssociatedPersonsForm lens={lens.focus('associatedPersons')} />
      <CorporateInsiderForm lens={lens.focus('corporateInsider')} />
      <PoliticallyExposedPersonForm lens={lens.focus('politicallyExposed')} />
    </VerticalStack>
  );
}
